import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import "./axios-setup";  // registers bearer-token fallback + withCredentials
import "./switcher-coupon-axios";  // V186 — auto-inject SWITCH50-* coupon on checkout calls
import axios from "axios";
import {
  Search, Zap, CheckCircle, AlertCircle, TrendingUp, Shield,
  Globe, Clock,
} from "lucide-react";

import { API } from "./config";
import { useAuth } from "./contexts/AuthContext";
import { useLanguage } from "./contexts/LanguageContext";

import { EmailCaptureModal } from "./components/common/EmailCaptureModal";
import { LoadingScreen } from "./components/common/LoadingScreen";
import { PhoneFloatingCTA } from "./components/common/PhoneFloatingCTA";
import { ChatWidget } from "./components/tools/ChatWidget";
import { LiveCounter } from "./components/LiveCounter";
import { TrustBadgeRow } from "./components/TrustBadgeRow";
import HomepageLossHero from "./components/HomepageLossHero";
import HomepageManifestoBanner from "./components/HomepageManifestoBanner";
import AgentOsHero from "./components/AgentOsHero";
import SwitcherLeaderboardBanner from "./components/SwitcherLeaderboardBanner";
import HomeAgentMarketplaceStrip from "./components/HomeAgentMarketplaceStrip";
import AutonomyLiveTicker from "./components/AutonomyLiveTicker";
import { SEOToolsSection } from "./components/tools/SEOToolsSection";
import { CompetitorComparison } from "./components/tools/CompetitorComparison";
import { BlogGenerator } from "./components/tools/BlogGenerator";
import { BlogPackGenerator } from "./components/tools/BlogPackGenerator";
import { RevenueCalculator } from "./components/tools/RevenueCalculator";
import { SocialProofPopup } from "./components/common/SocialProofPopup";
import { ABTestReport } from "./components/analysis/ABTestReport";
import { AnalysisResults } from "./components/analysis/AnalysisResults";
import { AdminDashboard } from "./components/admin/AdminDashboard";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { PriceComparisonSection } from "./components/marketing/PriceComparisonSection";
import { FearSection } from "./components/marketing/FearSection";
import { SocialProofBar, TrustBadges, HowItWorks, FloatingCTA } from "./components/marketing/ConversionBoosters";
import { PulseAIEngineBadge, PulseAIEngineShowcase } from "./components/marketing/PulseAIEngineBranding";
import LiveActivityFeed from "./components/marketing/LiveActivityFeed";
import HeroLiveMetricsStrip from "./components/marketing/HeroLiveMetricsStrip";
import FAQSection from "./components/marketing/FAQSection";
import SleepAISection from "./components/marketing/SleepAISection";
import SEOCourseSection from "./components/marketing/SEOCourseSection";
import SEOHead from "./components/SEOHead";

import { getHeroVariant, getHeroQuestionText, trackHeroEvent } from "./utils/abHero";

import { LandingHeader } from "./landing/LandingHeader";
import { LandingFooter } from "./landing/LandingFooter";
import {
  LandingMarketingSections,
  PluginsSection,
  ResultsStatsSection,
  ReviewsSection,
  PricingSection,
} from "./landing/LandingMarketingSections";
import { DEFAULT_LANGUAGES } from "./landing/constants";

function App() {
  const { user, loginWithGoogle } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [languages, setLanguages] = useState(DEFAULT_LANGUAGES);
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [analysisData, setAnalysisData] = useState(null);
  const [error, setError] = useState('');
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [abReport, setAbReport] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pendingUrl, setPendingUrl] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [cooldown, setCooldown] = useState(null);
  const [scanCounter, setScanCounter] = useState(26519);
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const [heroVariant] = useState(() => getHeroVariant());

  // Fire hero impression event once per session per (variant, language).
  useEffect(() => {
    trackHeroEvent("impression", heroVariant, language);
  }, [heroVariant, language]);

  const handleCheckout = async (packageId) => {
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
        user_email: user?.email || "",
        language,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      console.error("Checkout error:", err);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const langRes = await axios.get(`${API}/languages`);
        setLanguages(langRes.data.languages);
      } catch {}
      try {
        const counterRes = await axios.get(`${API}/stats/counter`);
        setScanCounter(counterRes.data.count);
      } catch {}
      if (user?.email) {
        try {
          const cdRes = await axios.get(`${API}/scan-cooldown?email=${encodeURIComponent(user.email)}`);
          if (cdRes.data.has_cooldown) setCooldown(cdRes.data);
          else setCooldown(null);
        } catch {}
      }
    };
    init();
  }, [user]);

  useEffect(() => {
    const onScroll = () => setShowFloatingCTA(window.scrollY > 800);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnalyzeWithEmail = useCallback(async (email, marketingConsent = false) => {
    setShowEmailModal(false); setUserEmail(email); setIsLoading(true); setError(''); setAnalysisData(null);
    try {
      const cdRes = await axios.get(`${API}/scan-cooldown?email=${encodeURIComponent(email)}`, { withCredentials: true, timeout: 15000 });
      if (cdRes.data.has_cooldown) {
        setCooldown(cdRes.data);
        setIsLoading(false);
        return;
      }
    } catch {}
    for (let i = 0; i < 4; i++) { setLoadingStep(i); await new Promise(r => setTimeout(r, 1000)); }
    const targetUrl = (pendingUrl || url || '').trim();
    if (!targetUrl) { setIsLoading(false); setError(t.err_no_url || 'Please enter a URL'); return; }
    try {
      const response = await axios.post(`${API}/analyze`, { url: targetUrl, language, email, marketing_consent: marketingConsent }, { withCredentials: true, timeout: 60000 });
      if (!user && response.data.analysis_id) {
        setIsLoading(false);
        window.location.href = `/results/${response.data.analysis_id}`;
        return;
      }
      setAnalysisData(response.data);
      // High-intent moment — user just got their result. Emit a hook
      // that PushPermissionPrompt listens for so the mobile-push opt-in
      // appears the moment they're engaged (vs the 4s default timer).
      try { window.dispatchEvent(new CustomEvent("trp:scan-completed")); } catch { /* ignore */ }
      // V218 — hero A/B: scan actually completed → conversion event.
      trackHeroEvent("scan_complete", heroVariant, language);
      try {
        const h = await axios.get(`${API}/analysis-history/${encodeURIComponent(targetUrl)}`);
        setAnalysisHistory(h.data.analyses || []);
      } catch { setAnalysisHistory([]); }
      setTimeout(() => {
        const el = document.getElementById("scan-results-anchor");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    } catch (e) {
      // Cooldown path — backend wraps a JSON-encoded object in the `detail`
      // field. Guard against non-JSON detail AND bare status=429 (we still
      // show a cooldown banner with best-effort values instead of a cryptic
      // English "An error occurred.").
      if (e.response?.status === 429) {
        const rawDetail = e.response?.data?.detail;
        let cd = null;
        if (typeof rawDetail === "string") {
          try { cd = JSON.parse(rawDetail); } catch {}
        } else if (rawDetail && typeof rawDetail === "object") {
          cd = rawDetail;
        }
        setCooldown({ has_cooldown: true, ...(cd || {}) });
        setIsLoading(false);
        return;
      }
      // No response at all → network error / timeout / request aborted.
      if (!e.response) {
        setError(
          e.code === "ECONNABORTED" || /timeout/i.test(e.message || "")
            ? (t.err_timeout || "Skenovanie trvá príliš dlho. Skús to prosím znova.")
            : (t.err_network || "Problém so spojením. Skontroluj internet a skús znova.")
        );
        return;
      }
      // Response exists → surface server detail if it's a plain string,
      // otherwise fall back to a localized generic message.
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" && d.length > 0 && d.length < 300
        ? d
        : (t.err_generic || "Skenovanie zlyhalo. Skús to znova.");
      setError(msg);
    }
    finally { setIsLoading(false); setPendingUrl(''); }
  }, [url, pendingUrl, language, user, t, heroVariant]);

  const handleAnalyzeClick = useCallback(() => {
    if (!url.trim()) return;
    if (cooldown?.has_cooldown) return;
    if (user && user.email) { handleAnalyzeWithEmail(user.email); return; }
    if (userEmail) { handleAnalyzeWithEmail(userEmail); return; }
    setPendingUrl(url.trim());
    setShowEmailModal(true);
  }, [url, userEmail, user, cooldown, handleAnalyzeWithEmail]);

  const handleCompare = useCallback(async (beforeId, afterId) => {
    setAbReport(null);
    try {
      const r = await axios.post(`${API}/compare-analyses?before_id=${beforeId}&after_id=${afterId}&language=${language}`);
      setAbReport(r.data);
    } catch { setError('Could not generate comparison.'); }
  }, [language]);

  if (showAdmin) {
    return (
      <>
        <AdminDashboard t={t} language={language} onBack={() => setShowAdmin(false)} />
        <SocialProofPopup language={language} />
        <LiveActivityFeed />
        <FloatingCTA t={t} visible={showFloatingCTA} />
        <PWAInstallPrompt />
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen" style={{ background: "var(--color-bg)", color: "var(--color-text)" }}>
        <SEOHead
          slug=""
          title={t.hero_title ? `${t.hero_title} ${t.hero_title_highlight || ""} | TopRankPulse` : "TopRankPulse — AI SEO & Visibility Tracker"}
          description={t.hero_subtitle || "The only tool that tracks your visibility in ChatGPT, Perplexity & Gemini. Get a complete SEO, GEO & AI audit in seconds."}
        />

        {/* V223 — FOMO scarcity banner is now rendered INSIDE LandingHeader
            so it always sits visibly at the very top of the fixed header bar. */}
        <LandingHeader
          t={t} user={user} language={language} setLanguage={setLanguage}
          languages={languages} onOpenAdmin={() => setShowAdmin(true)}
        />

        {/* HERO */}
        <section id="hero-section" className="relative min-h-screen flex items-center pt-20 themed-section" data-testid="hero-section">
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, var(--color-bg) 0%, var(--color-bg) 50%, var(--color-surface) 100%)" }} />
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/72857661-5b55-4534-a346-697fc1b969b9/images/5bba92b943be02aace6f324232889ee916b24aa7931ebbd4a6956dcc3c2995c4.png)`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
            <div className="max-w-4xl">
              <PulseAIEngineBadge />
              <p className="text-[#a855f7] font-['JetBrains_Mono'] text-sm tracking-[0.3em] uppercase mb-6" data-testid="hero-tag">{t.hero_crisis || "AI Visibility Crisis"}</p>
              <h1
                className="text-[2rem] xs:text-4xl sm:text-6xl lg:text-7xl font-black font-['Chivo'] tracking-tight leading-[1.05] mb-6 break-words pt-[0.1em]"
                style={{ textWrap: "balance", overflow: "visible", wordBreak: "normal", overflowWrap: "break-word", hyphens: "manual" }}
              >
                {t.hero_h1_1 || "73% OF WEBSITES ARE"}{" "}
                <span className="text-[#a855f7]">{t.hero_h1_highlight || "INVISIBLE"}</span>{" "}
                {t.hero_h1_2 || "TO AI SEARCH ENGINES"}
              </h1>
              <p className="text-xl text-[var(--color-text-muted)] mb-4 max-w-2xl font-['JetBrains_Mono']" data-testid={`hero-question-variant-${heroVariant}`}>{getHeroQuestionText(t, heroVariant)}</p>
              <p className="text-[var(--color-text-muted)] mb-6 max-w-2xl">{t.hero_desc || "ChatGPT, Perplexity, and Gemini are replacing Google for millions of users. If they don't cite your website, you're losing customers every single day."}</p>

              {/* One-Number Hero — live social proof counter */}
              <div className="mb-8" data-testid="hero-live-counter">
                <LiveCounter language={language} />
              </div>

              {cooldown?.has_cooldown ? (
                <div className="max-w-2xl mb-4 bg-[var(--color-surface)] border-2 border-[#FFE600] p-6" data-testid="cooldown-banner">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-6 h-6 text-[#FFE600]" />
                    <h3 className="text-lg font-bold font-['Cabinet_Grotesk']">{t.cd_title || "Next free scan available in"}</h3>
                  </div>
                  <div className="flex gap-4 mb-4">
                    <div className="bg-[var(--color-bg)]/50 px-4 py-3 text-center min-w-[70px]">
                      <p className="text-3xl font-black font-['Cabinet_Grotesk'] text-[#FFE600]">{cooldown.days_left || 0}</p>
                      <p className="text-[10px] text-[var(--color-text-muted)] uppercase">{t.cd_days || "days"}</p>
                    </div>
                    <div className="bg-[var(--color-bg)]/50 px-4 py-3 text-center min-w-[70px]">
                      <p className="text-3xl font-black font-['Cabinet_Grotesk'] text-[#FFE600]">{cooldown.hours_left || 0}</p>
                      <p className="text-[10px] text-[var(--color-text-muted)] uppercase">{t.cd_hours || "hours"}</p>
                    </div>
                  </div>
                  {cooldown.scanned_url && <p className="text-xs text-[var(--color-text-muted)] mb-3">{t.cd_scanned || "Last scanned"}: {cooldown.scanned_url}</p>}
                  <p className="text-sm text-[var(--color-text-muted)] mb-4">{t.cd_upgrade || "Want to scan more websites? Upgrade your plan."}</p>
                  <a href="/#pricing" className="inline-block px-6 py-3 bg-[#FFE600] text-black font-black uppercase tracking-wider hover:bg-yellow-300 transition-colors">
                    {t.cd_buy || "Upgrade Now"} — {t.discount_badge || "75% OFF"}
                  </a>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mb-4">
                  <input
                    data-testid="url-input"
                    type="text" value={url} onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAnalyzeClick(); }}
                    placeholder={t.placeholder || "Enter your website URL..."}
                    className="flex-1 px-6 py-4 bg-[var(--color-surface)] border-2 border-[var(--color-border)] text-white text-lg font-['JetBrains_Mono'] focus:border-[#a855f7] focus:outline-none placeholder:text-[var(--color-text-muted)]"
                  />
                  <button
                    data-testid="analyze-button"
                    onClick={handleAnalyzeClick} disabled={isLoading || !url.trim()}
                    className="px-8 py-4 bg-[#a855f7] text-white font-black text-lg uppercase tracking-wider hover:bg-purple-600 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
                  >
                    <Search className="w-5 h-5" />
                    {t.scan_free || "SCAN FREE"}
                  </button>
                </div>
              )}
              <p className="text-[var(--color-text-muted)] text-sm font-['JetBrains_Mono']">{t.no_credit || "No credit card. Instant results."}</p>
              {error && (
                <div className="mt-4 p-3 bg-[var(--color-surface)] border border-[#a855f7]/40 rounded-md flex items-start gap-3" data-testid="scan-error-block">
                  <AlertCircle className="w-5 h-5 text-[#a855f7] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-white" data-testid="scan-error-text">{error}</p>
                    {url.trim() && (userEmail || (user && user.email)) && (
                      <button
                        onClick={() => { setError(''); handleAnalyzeWithEmail((user && user.email) || userEmail); }}
                        disabled={isLoading}
                        data-testid="scan-retry-btn"
                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#a855f7] text-white rounded hover:bg-purple-600 disabled:opacity-50"
                      >
                        <Search className="w-3.5 h-3.5" />
                        {t.err_retry || "Skús znova"}
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-6 mt-12 text-[var(--color-text-muted)] text-sm font-['JetBrains_Mono']">
                <span className="flex items-center gap-2" data-testid="scan-counter"><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="text-emerald-400 font-bold">{scanCounter.toLocaleString()}</span> {t.trust_scans || "scans completed"}</span>
                <span className="flex items-center gap-2"><Shield className="w-4 h-4" />{t.trust_ssl || "SSL Encrypted"}</span>
                <span className="flex items-center gap-2"><Globe className="w-4 h-4" />{t.trust_langs || "26 Languages"}</span>
                <span className="flex items-center gap-2"><Zap className="w-4 h-4" />{t.trust_speed || "Results in 30 sec"}</span>
              </div>

              {/* V220 — RankWay killer: 4-metric KPI grid below trust line.
                  Pivots from THEIR vanity metrics (rankings/traffic) to OUR
                  outcomes (time saved, revenue earned, reviews, hot leads). */}
              <HeroLiveMetricsStrip language={language} />

              <SocialProofBar t={t} />
              <TrustBadges t={t} />

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                {!user && (
                  <button
                    onClick={loginWithGoogle}
                    className="flex items-center gap-3 px-6 py-3 bg-white text-gray-800 font-bold text-sm hover:bg-gray-100 transition-colors"
                    data-testid="hero-google-login-btn"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                    {t.hero_google || "Continue with Google"}
                  </button>
                )}
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-[var(--color-text-muted)] font-['JetBrains_Mono'] uppercase tracking-widest mr-3">{t.hero_works_with || "Works with"}</span>
                  {["WordPress", "Shopify", "Webflow", "Wix", "Framer"].map((p, i) => (
                    <span key={i} className="text-[10px] px-2 py-1 bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text-muted)] font-['JetBrains_Mono'] font-bold">{p}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST BADGES */}
        <section className="border-t border-[var(--color-border)] bg-[var(--color-surface)]/40">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <TrustBadgeRow language={language} />
          </div>
        </section>

        {/* V183 — AGENT OS HERO (all-in-one mockup, pain-first, trust stack) */}
        {!analysisData && <AgentOsHero />}

        {/* V188 — SWITCHER LEADERBOARD (this-week's competitor switch-overs) */}
        {!analysisData && <SwitcherLeaderboardBanner />}

        {/* HOMEPAGE MANIFESTO BANNER — V167 war-cry strip, links to /manifest */}
        {!analysisData && <HomepageManifestoBanner />}

        {/* HOMEPAGE LOSS HERO — primary conversion driver: live calc + $9 CTA */}
        {!analysisData && <HomepageLossHero />}

        {/* AI AGENTS MARKETPLACE STRIP — surface Agent OS to every homepage visitor */}
        {!analysisData && <HomeAgentMarketplaceStrip />}

        {/* V176 — AI AUTONOMY LIVE TICKER — real-time agent activity counter */}
        {!analysisData && <AutonomyLiveTicker />}

        {/* ANALYSIS RESULTS */}
        <div id="scan-results-anchor" />
        {analysisData && <AnalysisResults data={analysisData} t={t} onCompare={handleCompare} analysisHistory={analysisHistory} language={language} userEmail={userEmail} />}
        {abReport && <ABTestReport report={abReport} t={t} />}
        {analysisData && <CompetitorComparison t={t} language={language} userEmail={userEmail} analysisUrl={analysisData?.url || url} />}

        {!analysisData && <PulseAIEngineShowcase t={t} />}

        {analysisData && (
          <section className="py-16 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]">
            <div className="max-w-5xl mx-auto space-y-8">
              <BlogGenerator t={t} language={language} userEmail={userEmail} analysisUrl={analysisData?.url || url} />
              <BlogPackGenerator t={t} language={language} userEmail={userEmail} />
            </div>
          </section>
        )}

        <HowItWorks t={t} />

        {/* REVENUE CALCULATOR */}
        <section className="py-24 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]" data-testid="calculator-section">
          <div className="max-w-7xl mx-auto">
            <p className="text-[#FFE600] font-['JetBrains_Mono'] text-sm tracking-[0.3em] uppercase mb-4">{t.calc_tag || "Revenue Impact Calculator"}</p>
            <h2 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">
              {t.calc_h1 || "HOW MUCH ARE YOU"} <span className="text-[#a855f7]">{t.calc_h1_highlight || "LOSING"}</span><br />{t.calc_h1_2 || "WITHOUT AI VISIBILITY?"}
            </h2>
            <p className="text-[var(--color-text-muted)] mb-12 max-w-2xl">{t.calc_desc || "Select your industry and see exactly how much revenue you're missing from AI search engines every month."}</p>
            <RevenueCalculator />
          </div>
        </section>

        <LandingMarketingSections t={t} />

        <div className="bg-[var(--color-bg)]">
          <SEOToolsSection t={t} language={language} userEmail={userEmail} />
        </div>

        {!analysisData && <CompetitorComparison t={t} language={language} userEmail={userEmail} analysisUrl={url} />}

        <PriceComparisonSection t={t} />
        <FearSection t={t} />

        <PricingSection t={t} onCheckout={handleCheckout} />
        <PluginsSection t={t} />
        <ResultsStatsSection t={t} />
        <ReviewsSection t={t} />

        <SleepAISection />
        <SEOCourseSection />
        <FAQSection />

        <LandingFooter t={t} />

        <ChatWidget t={t} language={language} userEmail={userEmail} />
        {isLoading && <LoadingScreen t={t} step={loadingStep} />}
        <EmailCaptureModal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} onSubmit={handleAnalyzeWithEmail} t={t} isLoading={isLoading} targetUrl={pendingUrl || url} />
        <PhoneFloatingCTA language={language} />
      </div>
      <SocialProofPopup language={language} />
      <LiveActivityFeed />
      <FloatingCTA t={t} visible={showFloatingCTA} />
      <PWAInstallPrompt />
    </>
  );
}

export default App;
